import { type ReactNode, useRef } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Building2, ImagePlus, X } from 'lucide-react'

type VenueEvent = {
  id: string
  name: string | null
  event_type: string | null
  date: string | null
  status: string | null
  organization: { id: string; name: string }
}

type VenueOrganization = {
  id: string
  name: string
  events_count: number
  last_event_date: string | null
}

type Props = {
  venue: {
    id: string
    name: string
    city: string | null
    state: string | null
    address: string | null
    zip: string | null
    notes: string | null
    logo_url: string | null
  }
  events: VenueEvent[]
  organizations: VenueOrganization[]
  filters: { year: string }
  years: number[]
}

const ANY = '__any__'

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function ResearchVenueShow({ venue, events, organizations, filters, years }: Props) {
  const location = [venue.address, venue.city, venue.state, venue.zip].filter(Boolean).join(', ')
  const logoInputRef = useRef<HTMLInputElement>(null)

  function setYear(year: string) {
    router.get(
      `/admin/research/venues/${venue.id}`,
      year ? { year } : {},
      { preserveState: true },
    )
  }

  function uploadLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    router.patch(`/admin/research/venues/${venue.id}`, { research_venue: { logo: file } })
    e.target.value = ''
  }

  function removeLogo() {
    router.patch(`/admin/research/venues/${venue.id}`, {
      research_venue: { remove_logo: 'true' },
    })
  }

  return (
    <>
      <Head title={`Research - ${venue.name}`} />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="group relative">
              {venue.logo_url ? (
                <>
                  <img
                    src={venue.logo_url}
                    alt={`${venue.name} logo`}
                    className="size-16 rounded-xl object-cover ring-1 ring-black/10"
                  />
                  <button
                    type="button"
                    onClick={removeLogo}
                    title="Remove logo"
                    className="absolute -top-1.5 -right-1.5 hidden rounded-full bg-foreground p-0.5 text-background group-hover:block"
                  >
                    <X className="size-3" />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  title="Add logo"
                  className="flex size-16 items-center justify-center rounded-xl bg-primary/8 text-primary/40 ring-1 ring-black/5 transition-colors hover:bg-primary/15"
                >
                  <Building2 className="size-7" />
                </button>
              )}
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-semibold tracking-tight">{venue.name}</h2>
                <Badge variant="secondary">
                  {events.length} {events.length === 1 ? 'event' : 'events'}
                </Badge>
                <Badge variant="secondary">
                  {organizations.length}{' '}
                  {organizations.length === 1 ? 'organization' : 'organizations'}
                </Badge>
              </div>
              {location && <p className="mt-1 text-sm text-muted-foreground">{location}</p>}
              {venue.notes && <p className="mt-1 text-sm text-muted-foreground">{venue.notes}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => logoInputRef.current?.click()}>
              <ImagePlus className="mr-2 size-4" />
              {venue.logo_url ? 'Change logo' : 'Add logo'}
            </Button>
            <Select
              value={filters.year || ANY}
              onValueChange={value => setYear(value === ANY ? '' : value)}
            >
              <SelectTrigger className="w-32" size="sm">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent position="popper" align="end">
                <SelectGroup>
                  <SelectItem value={ANY}>Year: All</SelectItem>
                  {years.map(year => (
                    <SelectItem key={year} value={String(year)}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={uploadLogo}
        />

        <div className="mt-6 grid grid-cols-3 gap-6">
          <Card className="self-start">
            <CardHeader>
              <CardTitle>Organizations hosted here</CardTitle>
            </CardHeader>
            <CardContent>
              {organizations.length === 0 ? (
                <p className="text-sm text-muted-foreground">No organizations yet.</p>
              ) : (
                <ul className="space-y-3">
                  {organizations.map(org => (
                    <li key={org.id} className="flex items-start justify-between gap-3 text-sm">
                      <div>
                        <Link
                          href={`/admin/research/organizations/${org.id}`}
                          className="font-medium hover:underline"
                        >
                          {org.name}
                        </Link>
                        <p className="text-xs text-muted-foreground">
                          Last event: {formatDate(org.last_event_date)}
                        </p>
                      </div>
                      <Badge variant="outline">
                        {org.events_count} {org.events_count === 1 ? 'event' : 'events'}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <div className="col-span-2">
            <h3 className="mb-3 text-lg font-semibold tracking-tight">Events at this venue</h3>
            <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Organization</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.map(event => (
                    <TableRow key={event.id}>
                      <TableCell className="font-medium">
                        <Link
                          href={`/admin/research/organizations/${event.organization.id}`}
                          className="hover:underline"
                        >
                          {event.organization.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link
                          href={`/admin/research/events/${event.id}`}
                          className="hover:underline"
                        >
                          {event.name || 'Untitled'}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{event.event_type || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(event.date)}</TableCell>
                      <TableCell>
                        {event.status ? <Badge variant="secondary">{event.status}</Badge> : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {events.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                        No events recorded at this venue yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

ResearchVenueShow.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
